import { readableValue } from "../lib/when";
import { useEffect, useRef, useState, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { ApiError, apiErrorMessage } from "../api/client";
import { chatApi, type ForkView, type RunView } from "./api";
import { UI, useLang } from "./lang";

type B = { en: string; ar: string };

const CHOICES = [
  { key: "approved", label: UI.approve },
  { key: "rejected", label: UI.reject },
  { key: "needs_more_info", label: UI.change },
] as const;

const S = {
  failedHead: { en: "Held — the draft failed its checks:", ar: "موقوف — لم تجتز المسودة فحوصها:" },
  read: { en: "Read the draft", ar: "اقرأ المسودة" },
  hide: { en: "Hide the draft", ar: "إخفاء المسودة" },
  release: { en: "Release anyway", ar: "اعتماد رغم ذلك" },
  again: { en: "Run again", ar: "تشغيل من جديد" },
  againReason: { en: "Asked for a fresh draft.", ar: "طُلبت مسودة جديدة." },
  releaseNote: { en: "You are releasing a draft that failed its checks. Say why.", ar: "أنت تعتمد مسودة لم تجتز فحوصها. اذكر السبب." },
  waiting: { en: "Waiting", ar: "بانتظار" },
  prepared: { en: "Prepared", ar: "مُعدّ" },
  suggested: { en: "Suggested", ar: "مقترح" },
  group: { en: "Resolve all", ar: "حسم الكل" },
  similar: { en: "similar", ar: "مشابهة" },
  one: { en: "Just this one", ar: "هذا فقط" },
  recorded: { en: "recorded", ar: "سُجّلت" },
  already: { en: "already decided", ar: "حُسمت سابقاً" },
  weSuggest: { en: "We suggest", ar: "نقترح" },
  rewriteHead: { en: "Proposed rewrite — passes the checks", ar: "صياغة مقترحة — اجتازت الفحوص" },
  noRewrite: { en: "No compliant rewrite yet.", ar: "لا توجد صياغة متوافقة بعد." },
  approveRewrite: { en: "Approve the rewrite", ar: "اعتماد الصياغة الجديدة" },
  recordGroup: { en: "Record", ar: "سجّل" },
  useSuggestion: { en: "Use the suggestion", ar: "اعتمد الاقتراح" },
  reasonHint: { en: "A sentence — at least 10 characters.", ar: "جملة — ١٠ أحرف على الأقل." },
  codeNeeded: { en: "This decision needs a code. We sent one to your email.", ar: "هذا القرار يحتاج إلى رمز. أرسلناه إلى بريدك." },
  codeLabel: { en: "Six-digit code", ar: "الرمز المكوّن من ستة أرقام" },
  codeAgain: { en: "Send again", ar: "أرسل مجدداً" },
  codeSending: { en: "Sending a code…", ar: "جارٍ إرسال الرمز…" },
} satisfies Record<string, B>;

/** One word per decision, as the thread says it afterwards. */
const PAST: Record<string, { en: string; ar: string }> = {
  approved: { en: "approved", ar: "تمت الموافقة عليها" },
  rejected: { en: "rejected", ar: "رُفضت" },
  needs_more_info: { en: "asked for more", ar: "طُلب المزيد" },
};

const proposalKey = (p: string) => (p === "approve" ? "approved" : p === "reject" ? "rejected" : "needs_more_info");

/** The draft with each figure no calculation produced marked, so the approver sees what failed. */
function marked(text: string, numbers: string[]): ReactNode {
  if (numbers.length === 0) return text;
  const esc = numbers.map((n) => n.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")).sort((a, b) => b.length - a.length);
  const re = new RegExp(`(?<![\\d.])(${esc.join("|")})(?![\\d])`, "g");
  const out: ReactNode[] = [];
  let last = 0;
  for (const m of text.matchAll(re)) {
    out.push(text.slice(last, m.index));
    out.push(<mark key={m.index}>{m[0]}</mark>);
    last = (m.index ?? 0) + m[0].length;
  }
  out.push(text.slice(last));
  return out;
}

/** Why this needs a person, in the rule's own sentence when there is one. */
function why(f: ForkView): B {
  const rule = f.blocking_rules[0] ?? f.missing_rules.find((r) => r.summary);
  if (rule?.summary) return { en: rule.summary, ar: rule.summary_ar ?? rule.summary };
  return f.decision
    ? { en: `${f.decision.label} is held for sign-off.`, ar: `${f.decision.label_ar ?? f.decision.label} بانتظار الاعتماد.` }
    : { en: "Held for a decision.", ar: "بانتظار قرار." };
}

function ago(iso: string, lang: "en" | "ar"): string {
  const mins = Math.max(0, Math.round((Date.now() - Date.parse(iso)) / 60_000));
  if (mins < 1) return lang === "ar" ? "الآن" : "just now";
  if (mins < 60) return lang === "ar" ? `منذ ${mins} د` : `${mins} min`;
  const h = Math.round(mins / 60);
  if (h < 48) return lang === "ar" ? `منذ ${h} س` : `${h} h`;
  return lang === "ar" ? `منذ ${Math.round(h / 24)} يوم` : `${Math.round(h / 24)} days`;
}

/**
 * A decision, as a native card with fixed controls (NEW-UX-5), carrying what
 * a person needs to decide (audit fix 3): the figures code computed, why it is
 * held, what Approve will do, and one tap to how it was made. A draft that
 * failed its checks is not approved by default (audit fix 1): the choices are
 * reject, run again, or — only after reading it — release it anyway.
 */
export function ForkCard({ fork, run, workspaceId }: { fork: ForkView; run?: RunView | null; workspaceId: string }) {
  const { t, lang } = useLang();
  const qc = useQueryClient();
  const [decision, setDecision] = useState<string | null>(null);
  const [release, setRelease] = useState(false);
  // Approving the checked rewrite of a blocked ad decides the rewrite's own run (demo step 4).
  const [rewriteMode, setRewriteMode] = useState(false);
  const [reading, setReading] = useState(false);
  const [reason, setReason] = useState("");
  const reasonRef = useRef<HTMLTextAreaElement | null>(null);
  const [grouped, setGrouped] = useState(false);
  // Step-up (NEW-CS-5): set once the server asks for a code; the code is sent then.
  const [stepUp, setStepUp] = useState(false);
  const [code, setCode] = useState("");
  const [batchDone, setBatchDone] = useState<{ ok: number; already: number; other: number } | null>(null);
  const prepared = run?.prepared ?? null;
  const similar = prepared?.similar_run_ids ?? [];
  const failed = (run?.checks_failed.length ?? 0) > 0;
  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ["chat-thread", workspaceId] });
    void qc.invalidateQueries({ queryKey: ["chat-queue", workspaceId] });
  };
  const batch = useMutation({
    mutationFn: () =>
      chatApi.batchResolve(workspaceId, { run_ids: [fork.run_id, ...similar], decision: decision!, reason: reason.trim() }),
    onSuccess: (r) => {
      setBatchDone({
        ok: r.results.filter((x) => x.ok).length,
        already: r.results.filter((x) => x.error === "already_resolved").length,
        other: r.results.filter((x) => !x.ok && x.error !== "already_resolved").length,
      });
      refresh();
    },
  });
  const sendCode = useMutation({ mutationFn: () => chatApi.stepUp(workspaceId, fork.run_id) });
  // The code is for approving only: rejecting stays one tap (NEW-CS-5).
  const approving = release || decision === "approved";
  const resolve = useMutation({
    mutationFn: () =>
      rewriteMode && run?.rewrite
        ? chatApi.resolve(workspaceId, run.rewrite.run_id, { decision: "approved", reason: reason.trim() })
        : chatApi.resolve(workspaceId, fork.run_id, {
        decision: release ? "approved" : decision!,
        reason: reason.trim(),
        ...(release ? { release_failed_draft: true } : {}),
        ...(stepUp && approving ? { code } : {}),
      }),
    onSuccess: refresh,
    onError: (e) => {
      const err = e instanceof ApiError ? (e.body as { error?: string } | null)?.error : undefined;
      // The first ask for a code is a step, not an error: send one and show the field.
      if (err === "step_up_required" && !stepUp) {
        setStepUp(true);
        sendCode.mutate();
      }
    },
  });
  const askedForCode =
    resolve.error instanceof ApiError && (resolve.error.body as { error?: string } | null)?.error === "step_up_required" && !code;
  const again = useMutation({
    mutationFn: async () => {
      // A fresh draft, not a verdict on the decision: no standing approval is suspended.
      await chatApi.resolve(workspaceId, fork.run_id, { decision: "rejected", reason: t(S.againReason), run_again: true });
      await chatApi.send(workspaceId, { text: fork.hand_name, chip: fork.hand_key });
    },
    onSuccess: refresh,
  });
  // Choosing is the start of deciding: bring the reason box into view and focus it.
  useEffect(() => {
    if (!decision && !release) return;
    const el = reasonRef.current;
    if (!el) return;
    el.focus();
    el.scrollIntoView?.({ block: "nearest", behavior: "smooth" });
  }, [decision, release]);
  const id = `fork-${fork.run_id}`;
  const chosen = release || rewriteMode || decision !== null;
  const draft = run ? (lang === "ar" ? run.review_draft_ar ?? run.review_draft_en : run.review_draft_en) : null;
  return (
    <div className="pwa-card pwa-fork" data-testid={id}>
      <div className="pwa-fork-head">
        <div className="ttl">{t({ en: fork.hand_name, ar: fork.hand_name_ar ?? fork.hand_name })}</div>
        <span className="pwa-fork-age">
          {t(S.waiting)} {ago(fork.created_at, lang)}
        </span>
      </div>
      <div className="sub">
        {fork.subject ? `${t({ en: fork.subject.label, ar: fork.subject.label_ar ?? fork.subject.label })}: ${readableValue(fork.subject.value)}` : null}
        {fork.decision ? ` · ${t({ en: fork.decision.label, ar: fork.decision.label_ar ?? fork.decision.label })}` : null}
      </div>

      {run && run.tiles.length > 0 ? (
        <div className="pwa-tiles">
          {run.tiles.map((x) => (
            <div key={x.label_en} className="pwa-tile">
              <div className="lbl">{t({ en: x.label_en, ar: x.label_ar })}</div>
              <div className="val">{x.value}</div>
            </div>
          ))}
        </div>
      ) : null}

      {failed ? (
        <div className="why">
          {t(S.failedHead)}
          <ul className="pwa-fork-failed">
            {(lang === "ar" ? run!.checks_failed_ar : run!.checks_failed_en).map((c) => (
              <li key={c}>{c}</li>
            ))}
          </ul>
        </div>
      ) : (
        <div className="why">{t(why(fork))}</div>
      )}
      {run && (run.evidence ?? []).length > 0 ? (
        <ul className="pwa-evidence" data-testid={`${id}-evidence`}>
          {run.evidence!.map((e, i) => (
            <li key={i}>
              <bdi>{t(e.label)}</bdi>: <bdi>{e.value}</bdi>
              {e.detail ? <> — {t(e.detail)}</> : null}
            </li>
          ))}
        </ul>
      ) : null}
      {run?.rules_note ? <div className="sub">{t(run.rules_note)}</div> : null}
      {run?.rewrite && !run.rewrite.decided ? (
        <div className="pwa-fork-rewrite" data-testid={`${id}-rewrite-box`}>
          {run.rewrite.passed ? (
            <>
              <div className="pwa-fork-prepared-label">{t(S.rewriteHead)}</div>
              <bdi className="pwa-fork-draft">{lang === "ar" ? run.rewrite.copy_ar ?? run.rewrite.copy_en : run.rewrite.copy_en}</bdi>
            </>
          ) : (
            <div className="sub">{t(S.noRewrite)}</div>
          )}
        </div>
      ) : null}
      {run && !failed ? <div className="pwa-fork-effect">{t(run.approve_effect)}</div> : null}

      {reading && draft ? <div className="pwa-fork-draft">{marked(draft, run?.unmatched_numbers ?? [])}</div> : null}

      {prepared && !failed ? (
        <div className="pwa-fork-prepared" data-testid={`${id}-prepared`}>
          {prepared.proposal !== "none" ? (
            <>
              <span className="pwa-fork-prepared-label">{t(S.weSuggest)}:</span>{" "}
              {t(CHOICES.find((c) => c.key === proposalKey(prepared.proposal))!.label)} — {t(prepared.basis)}
            </>
          ) : (
            t(prepared.basis)
          )}
        </div>
      ) : null}

      <div className="pwa-choices" role="group" aria-label="Decision">
        {failed ? (
          <>
            <button type="button" className={`pwa-btn${decision === "rejected" ? " on" : ""}`} data-testid={`${id}-rejected`}
              aria-pressed={decision === "rejected"} onClick={() => { setRewriteMode(false); setRelease(false); setDecision("rejected"); }}>
              {t(UI.reject)}
            </button>
            <button type="button" className="pwa-btn" data-testid={`${id}-again`} disabled={again.isPending} onClick={() => again.mutate()}>
              {t(S.again)}
            </button>
            {draft ? (
              <button type="button" className="pwa-btn" data-testid={`${id}-read`} onClick={() => setReading((r) => !r)}>
                {t(reading ? S.hide : S.read)}
              </button>
            ) : null}
            {reading ? (
              <button type="button" className={`pwa-btn${release ? " on" : ""}`} data-testid={`${id}-release`}
                aria-pressed={release} onClick={() => { setDecision(null); setRelease(true); }}>
                {t(S.release)}
              </button>
            ) : null}
          </>
        ) : (
          CHOICES.map((c) => (
            <button
              key={c.key}
              type="button"
              className={`pwa-btn${decision === c.key ? " on" : ""}`}
              aria-pressed={decision === c.key}
              data-testid={`${id}-${c.key}`}
              onClick={() => { setRewriteMode(false); setDecision(c.key); }}
            >
              {t(c.label)}
            </button>
          ))
        )}
        {prepared && !failed && prepared.proposal !== "none" ? (
          // A suggestion, greyed: adopted only when tapped (§6.1).
          <button type="button" className="pwa-btn suggest" data-testid={`${id}-suggest`}
            onClick={() => setDecision(prepared.proposal === "approve" ? "approved" : prepared.proposal === "reject" ? "rejected" : "needs_more_info")}>
            {t(S.useSuggestion)}
          </button>
        ) : null}
        {run?.rewrite?.passed && !run.rewrite.decided ? (
          <button type="button" className={`pwa-btn${rewriteMode ? " on" : ""}`} data-testid={`${id}-rewrite`} aria-pressed={rewriteMode}
            onClick={() => { setDecision(null); setRelease(false); setRewriteMode(true); }}>
            {t(S.approveRewrite)}
          </button>
        ) : null}
        {similar.length > 0 && !failed ? (
          <button type="button" className={`pwa-btn${grouped ? " on" : ""}`} data-testid={`${id}-group`} aria-pressed={grouped}
            onClick={() => setGrouped((g) => !g)}>
            {grouped ? t(S.one) : `${t(S.group)} ${similar.length + 1} ${t(S.similar)}`}
          </button>
        ) : null}
        <Link className="pwa-fork-how" to={`/app/${workspaceId}/runs/${fork.run_id}`}>
          {t(UI.howMade)}
        </Link>
      </div>

      {chosen ? (
        <>
          {release ? <div className="pwa-fork-effect">{t(S.releaseNote)} {run ? t(run.approve_effect) : null}</div> : null}
          <textarea
            ref={reasonRef}
            className="pwa-reason"
            data-testid={`${id}-reason`}
            placeholder={t(UI.reasonPlaceholder)}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
          />
          {reason.trim().length < 10 ? <div className="sub pwa-reason-hint">{t(S.reasonHint)}</div> : null}
          {stepUp && approving ? (
            <div className="pwa-fork-stepup" data-testid={`${id}-stepup`}>
              {/* Only say a code was sent once it was (demo-ready Task 8). */}
              {sendCode.isPending ? <div className="pwa-fork-effect">{t(S.codeSending)}</div> : null}
              {sendCode.data?.sent ? <div className="pwa-fork-effect">{t(S.codeNeeded)}</div> : null}
              {sendCode.data?.demo_code ? (
                <div className="pwa-fork-effect" data-testid={`${id}-demo-code`}>
                  {t({ en: `Demo: your code is ${sendCode.data.demo_code}`, ar: `عرض تجريبي: رمزك هو ${sendCode.data.demo_code}` })}
                </div>
              ) : null}
              {sendCode.isError ? <div className="sub" role="alert" style={{ color: "var(--danger)" }}>{apiErrorMessage(sendCode.error)}</div> : null}
              <input
                className="pwa-code"
                data-testid={`${id}-code`}
                aria-label={t(S.codeLabel)}
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={6}
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
              />
              <button type="button" className="pwa-btn" data-testid={`${id}-code-again`} disabled={sendCode.isPending}
                onClick={() => sendCode.mutate()}>
                {t(S.codeAgain)}
              </button>

            </div>
          ) : null}
          <div className="pwa-fork-submit">
            <button
              type="button"
              className="pwa-btn primary"
              data-testid={`${id}-submit`}
              disabled={resolve.isPending || batch.isPending || reason.trim().length < 10 || (stepUp && approving && code.length !== 6)}
              onClick={() => (grouped && !release ? batch.mutate() : resolve.mutate())}
            >
              {resolve.isPending || batch.isPending
                ? t(UI.recording)
                : grouped && !release && decision
                  ? `${t(S.recordGroup)} — ${t(CHOICES.find((c) => c.key === decision)!.label).toLowerCase()} ${similar.length + 1}`
                  : t(UI.record)}
            </button>
          </div>
        </>
      ) : null}
      {batchDone ? (
        <div className="sub" role="status" data-testid={`${id}-batch-result`}>
          {`${batchDone.ok} ${decision ? t(PAST[decision] ?? S.recorded) : t(S.recorded)}`}
          {batchDone.already > 0 ? ` · ${batchDone.already} ${t(S.already)}` : ""}
          {batchDone.other > 0 ? ` · ${batchDone.other} ✕` : ""}
        </div>
      ) : null}
      {(resolve.isError && !askedForCode) || again.isError || batch.isError ? (
        <div className="sub" role="alert" style={{ color: "var(--danger)", marginTop: 6 }}>
          {apiErrorMessage((resolve.isError && !askedForCode ? resolve.error : null) ?? again.error ?? batch.error)}
        </div>
      ) : null}
    </div>
  );
}
